/**
 * Environment configuration matching Flutter Env class
 * 
 * Vite exposes environment variables through import.meta.env
 * Variables must be prefixed with VITE_ to be exposed to the client
 * 
 * Usage:
 *   import { env } from './config/env';
 *   const cdnUrl = env.CDN_NAME;
 */

interface EnvConfig {
  // Domain Configuration
  DOMAIN_NAME: string;

  // CDN Configuration
  CDN_NAME: string;

  // Storage Bucket Prefixes
  CDN_BUCKET_PREFIX: string;
  DEFAULT_BUCKET_PREFIX: string;
  USER_PROFILES_BUCKET_PREFIX: string;

  // Bucket Names
  CDN_BUCKET_NAME: string;
  DEFAULT_BUCKET_NAME: string;
  USER_PROFILES_BUCKET_NAME: string;
  IDENTITY_BUCKET: string;
  GIFTS_BUCKET: string;

  // API Configuration
  API_BASE_URL: string;
}

/**
 * Get environment variable without exposing defaults
 * Returns empty string if not set - app should handle missing config gracefully
 */
function getEnvVar(key: string): string {
  const value = import.meta.env[key];

  if (!value) {
    console.error(`Environment variable ${key} is not set. Check your .env file.`);
    return '';
  }

  return value;
}

/**
 * Environment configuration object
 * Matches Flutter Env class structure
 * 
 * SECURITY: No default values are provided.
 * If .env file is missing or incomplete, empty strings are returned.
 * App will not work without proper environment configuration.
 */
export const env: EnvConfig = {
  // Domain Configuration
  DOMAIN_NAME: getEnvVar('VITE_DOMAIN_NAME'),

  // CDN Configuration
  CDN_NAME: getEnvVar('VITE_CDN_NAME'),

  // Storage Bucket Prefixes
  CDN_BUCKET_PREFIX: getEnvVar('VITE_CDN_BUCKET_PREFIX'),
  DEFAULT_BUCKET_PREFIX: getEnvVar('VITE_DEFAULT_BUCKET_PREFIX'),
  USER_PROFILES_BUCKET_PREFIX: getEnvVar('VITE_USER_PROFILES_BUCKET_PREFIX'),

  // Bucket Names
  CDN_BUCKET_NAME: getEnvVar('VITE_CDN_BUCKET_NAME'),
  DEFAULT_BUCKET_NAME: getEnvVar('VITE_DEFAULT_BUCKET_NAME'),
  USER_PROFILES_BUCKET_NAME: getEnvVar('VITE_USER_PROFILES_BUCKET_NAME'),
  IDENTITY_BUCKET: getEnvVar('VITE_IDENTITY_BUCKET'),
  GIFTS_BUCKET: getEnvVar('VITE_GIFTS_BUCKET'),

  // API Configuration
  API_BASE_URL: getEnvVar('VITE_API_BASE_URL'),
};

/**
 * Type-safe environment variable access
 */
export type Env = typeof env;